import {
	CustomQuoteCommentWrapper,
	CustomQuoteDesignerLabel,
	CustomQuoteHeaderDesignerRequestedBy,
	CustomQuoteHeaderDesignerUpdatedRow,
	CustomQuoteHeaderWrapper,
	CustomQuoteStatus,
	CustomQuoteStatusRow,
	HorizontalLine,
	LatestCommentContainer,
	LatestCommentContent,
	LatestCommentHeader
} from "./CustomQuoteDetailHeader.styles";
import { Button, Drawer } from "@mui/material";
import { Chip } from "components/Chip";
import CreateOutlinedIcon from "@mui/icons-material/CreateOutlined";
import { CommentText, NoCommentsYetText, UnavailableDataPlaceholderText } from "constants/text";
import { models } from "types/api/viewModels";
import { splitPascalCase } from "utils/string";
import {
	CustomQuoteStatusDescription,
	formatQuoteDesignerName,
	formatTimestamp,
	getCommentDisplayName
} from "../customQuoteUtils";
import { KeyboardEvent, MouseEvent, useState } from "react";
import CustomQuoteCommentsFlyout from "./CustomQuoteComments/CustomQuoteCommentsFlyout";
import Divider from "@mui/material/Divider";

interface CustomQuoteDetailHeaderProps {
	quote: models["CustomQuoteViewModel"] | undefined;
}

export const CustomQuoteDetailHeader = ({ quote }: CustomQuoteDetailHeaderProps) => {
	const [showCommentsDrawer, setShowCommentsDrawer] = useState(false);

	const toggleCommentsDrawer = (open: boolean) => (event: KeyboardEvent | MouseEvent) => {
		if (
			event?.type === "keydown" &&
			((event as KeyboardEvent)?.key === "Tab" || (event as KeyboardEvent)?.key === "Shift")
		) {
			return;
		}

		setShowCommentsDrawer(open);
	};

	const handleCommentsClick = () => {
		setShowCommentsDrawer(true);
	};

	const getCommentsDisplayText = () => {
		if (!quote?.comments || quote?.comments.length === 0) {
			return NoCommentsYetText;
		}

		// Find the latest comment by timestamp
		const latestComment = quote.comments.reduce((latest, current) => {
			if (!latest.timestamp || !current.timestamp) {
				return latest.timestamp ? latest : current;
			}
			return new Date(current.timestamp) > new Date(latest.timestamp) ? current : latest;
		});

		if (!latestComment) {
			return NoCommentsYetText;
		}

		return (
			<LatestCommentContainer>
				<LatestCommentHeader>{formatTimestamp(latestComment.timestamp)}</LatestCommentHeader>
				<LatestCommentContent>
					<span>{getCommentDisplayName(quote, latestComment)}:</span> {latestComment.comment}
				</LatestCommentContent>
			</LatestCommentContainer>
		);
	};

	return (
		<>
			<CustomQuoteHeaderWrapper>
				<CustomQuoteHeaderDesignerUpdatedRow>
					<CustomQuoteHeaderDesignerRequestedBy>
						<div>
							<CustomQuoteDesignerLabel>Designer #</CustomQuoteDesignerLabel>
							<span data-testid="custom-quote-designer-number">{formatQuoteDesignerName(quote)}</span>
						</div>
						<Divider
							orientation="vertical"
							flexItem
						/>
						<div>
							<CustomQuoteDesignerLabel>Requested By</CustomQuoteDesignerLabel>
							<span data-testid="custom-quote-requested-by">
								{quote?.requestedBy
									? `${quote.requestedBy.firstName} ${quote.requestedBy.lastName}`
									: UnavailableDataPlaceholderText}
							</span>
						</div>
					</CustomQuoteHeaderDesignerRequestedBy>
					<div className="overline">
						Status Updated:{" "}
						{quote?.updatedAt
							? new Date(quote?.updatedAt).toLocaleDateString()
							: UnavailableDataPlaceholderText}
					</div>
				</CustomQuoteHeaderDesignerUpdatedRow>
				<HorizontalLine />
				<CustomQuoteStatusRow>
					{quote?.status && (
						<>
							<Chip
								visible
								label={quote?.status ? splitPascalCase(quote.status).join(" ") : ""}
							/>
							<CustomQuoteStatus className="subtitle2">
								{CustomQuoteStatusDescription[quote.status]}
							</CustomQuoteStatus>
						</>
					)}
				</CustomQuoteStatusRow>
				<CustomQuoteCommentWrapper>
					<div>
						<div>
							<span className="overline">{CommentText}</span>
						</div>
						<div>{getCommentsDisplayText()}</div>
					</div>
					<Button
						startIcon={<CreateOutlinedIcon />}
						sx={{ textTransform: "uppercase" }}
						variant="text"
						onClick={handleCommentsClick}
						data-testid="custom-quote-comments-button"
					>
						{CommentText}
					</Button>
				</CustomQuoteCommentWrapper>
			</CustomQuoteHeaderWrapper>
			<Drawer
				anchor="right"
				open={showCommentsDrawer}
				onClose={toggleCommentsDrawer(false)}
			>
				<CustomQuoteCommentsFlyout
					quote={quote}
					closeDrawer={() => setShowCommentsDrawer(false)}
				/>
			</Drawer>
		</>
	);
};
